import { IconDotsVertical } from '@tabler/icons-react';
import classNames from 'classnames';
import * as Flowbite from 'flowbite-react';
import {
    Fragment,
    PropsWithChildren,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
    generatePath,
    Link,
    LinkProps,
    useLocation,
    useMatch,
} from 'react-router';
import { siteNavigationData } from 'pages/Site/siteNavigationData';
import Dropdown, { DropdownItem } from 'shared/Dropdown';
import DoubleBadge from 'shared/DoubleBadge';
import { ToggleSwitch } from 'shared/Input';
import { NavigationSite } from 'types/Navigation';
import { SITE_ROUTE_PATH } from 'utils/constants';
import { usePut } from 'utils/hooks/useAPI';
import isEllipsisActive from 'utils/isEllipsisActive';
import { useSiteSelectorContext } from '../SiteSelectorContext';
import { SiteStatusBadge } from 'components/Badges/SiteStatusBadge';
import { getSharedQueryParams } from 'utils/queryParams';

interface SiteListItemProps extends Omit<LinkProps, 'to'> {
    site: NavigationSite;
}

const NAVIGATION_SITES_SITE_ID_SITE_ALERT_API =
    '/helios-api/navigation/sites/:site_id/site-alert';

export const SiteListItem = ({
    className,
    site: { alerts_muted, name, site_id, status, can_mute_alerts },
    ...props
}: SiteListItemProps) => {
    const { t } = useTranslation('navigation');

    const location = useLocation();
    const siteRoutePathMatch = useMatch(SITE_ROUTE_PATH);

    const pathname = siteRoutePathMatch
        ? generatePath(SITE_ROUTE_PATH, {
              siteId: String(site_id),
              group: siteRoutePathMatch.params.group || '',
              page: siteRoutePathMatch.params.page || '',
              subpage: siteRoutePathMatch.params.subpage || null,
              detail: null,
          })
        : generatePath(siteNavigationData.overview.link, {
              siteId: String(site_id),
          });

    const search = getSharedQueryParams(location.search);
    const siteItemUrl = siteRoutePathMatch ? { pathname, search } : pathname;

    const nameRef = useRef<HTMLSpanElement>(null);

    const { visible: siteSelectorExpandStarted } = useSiteSelectorContext();
    const [visible, setVisible] = useState(true);

    const Wrapper = useCallback(
        ({ children }: PropsWithChildren) =>
            isEllipsisActive(nameRef.current) ? (
                <Flowbite.Tooltip
                    className="w-max z-(--z-dialog)"
                    content={name}
                    placement="right"
                    theme={{ target: 'w-auto' }}
                >
                    {children}
                </Flowbite.Tooltip>
            ) : (
                <Fragment>{children}</Fragment>
            ),
        [name]
    );

    useEffect(() => {
        requestAnimationFrame(() => setVisible(siteSelectorExpandStarted));
    }, [siteSelectorExpandStarted]);

    // Tracks mounted state to prevent sending a PUT request on component mount.
    const isMountedRef = useRef(false);

    /*
     * Tracks reverting of `muteAlerts` state in case of an API error to prevent
     * an infinite loop explained below:
     * [1] set -> [2] put -> [3] error -> [1] revert (i.e. set) -> [2] put -> ...
     */
    const isRevertingMuteAlertsRef = useRef(false);

    const [muteAlerts, setMuteAlerts] = useState(alerts_muted);

    const [{ loading }, putMuteAlerts] = usePut<never>(
        generatePath(NAVIGATION_SITES_SITE_ID_SITE_ALERT_API, {
            site_id: String(site_id),
        })
    );

    useEffect(() => {
        (async () => {
            if (isMountedRef.current && !isRevertingMuteAlertsRef.current) {
                try {
                    await putMuteAlerts({ data: { mute_alerts: muteAlerts } });
                } catch (exception) {
                    isRevertingMuteAlertsRef.current = true;
                    setMuteAlerts((currentMuteAlerts) => !currentMuteAlerts);
                }
            }
        })();
    }, [muteAlerts, putMuteAlerts]);

    useEffect(() => {
        isMountedRef.current = true;

        return () => {
            isMountedRef.current = false;
        };
    }, []);
    return (
        <Wrapper>
            <Link
                className={classNames(
                    'border-b border-gray-200 duration-200 gap-1 inline-flex items-center no-underline px-1 py-1.5 transition w-[calc(100%_-_20px)] hover:bg-gray-100 focus-visible:bg-gray-100 [&:hover_+_*]:bg-gray-100 [&:focus-visible_+_*]:bg-gray-100',
                    className
                )}
                state={{ site: { name, status } }}
                to={siteItemUrl}
                {...props}
            >
                <SiteStatusBadge
                    iconOnly
                    className="flex-none size-4"
                    status={status}
                />
                <span
                    ref={nameRef}
                    className="flex-1 font-medium overflow-hidden text-ellipsis text-gray-600 text-sm whitespace-nowrap"
                >
                    {name}
                </span>
                <DoubleBadge>{site_id}</DoubleBadge>
            </Link>

            <span className="border-b border-gray-200 duration-200 inline-block transition [&_>_button]:border-none [&_>_button]:rounded-xs [&_>_button_>_span]:pr-1 [&_>_button_>_span]:px-0 [&_>_button_>_span]:py-2">
                <Dropdown
                    aria-label={t(
                        'site_selector.lists.site_list_item.context_menu.label'
                    )}
                    arrowIcon={false}
                    dismissOnClick={false}
                    label={<IconDotsVertical className="h-4 w-4" />}
                >
                    {can_mute_alerts && (
                        <DropdownItem as="span">
                            <ToggleSwitch
                                /*
                                 * Use negative margins with paddings to make the toggle
                                 * switch clickable in the entire `Dropdown.Item`
                                 * component, not just within its standard bounds.
                                 */
                                className="-mx-4 -my-2 px-4 py-2 lowercase"
                                disabled={loading}
                                label={t(
                                    'site_selector.lists.site_list_item.context_menu.mute_alerts'
                                )}
                                value={muteAlerts}
                                onChange={() => {
                                    isRevertingMuteAlertsRef.current = false;
                                    setMuteAlerts(
                                        (currentMuteAlerts) =>
                                            !currentMuteAlerts
                                    );
                                }}
                            />
                        </DropdownItem>
                    )}

                    <DropdownItem
                        as={Link}
                        className="gap-3"
                        state={{ site: { name, status } }}
                        to={generatePath(
                            siteNavigationData.configuration.link,
                            { siteId: String(site_id) }
                        )}
                    >
                        {siteNavigationData.configuration.icon ? (
                            <siteNavigationData.configuration.icon className="h-4 w-4" />
                        ) : (
                            <></>
                        )}
                        {t('site_navigation.configuration')}
                    </DropdownItem>
                </Dropdown>
            </span>
        </Wrapper>
    );
};
